import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord } from '@warp-drive/utilities/json-api';
import type { Store } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type RouterService from '@ember/routing/router-service';
import type { AuthenticatedSyfRouteModel } from 'tio-employee/routes/authenticated/syf';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { MatchPlanExtension as MatchPlanModel } from 'tio-data/data/resources/match-plan.ext';
import type { MatchParticipantExtension as MatchParticipantModel } from 'tio-data/data/resources/match-participant.ext';

export type AuthenticatedSyfEnrollmentRouteModel = {
  person: PersonModel;
  matchParticipant: MatchParticipantModel;
  matchPlan: MatchPlanModel;
};

export default class AuthenticatedSyfAccountConfirmationRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;

  beforeModel() {
    const parentModel = this.modelFor('authenticated.syf') as AuthenticatedSyfRouteModel | null;
    // Null parent model -> no usable SYF benefit (no match participant, no
    // employee record, or the parent lookup failed, e.g. 403 on includes).
    // Leave the SYF tree entirely: bouncing to syf.dashboard here while
    // dashboard bounces unenrolled users back to enrollment created an
    // infinite redirect loop (the "Secure landing crashes" hang).
    if (!parentModel) {
      this.router.transitionTo('authenticated.dashboard');
      return;
    }
    if (parentModel.matchParticipant.isEnrolled) {
      this.router.transitionTo('authenticated.syf.dashboard');
    }
  }

  async model() {
    const { content } = (await this.store.request(
      findRecord<PersonModel>('person', this.sessionContext.person.id!, {
        include: 'accounts.financial-institution',
      })
    )) as { content: { data: PersonModel } };
    const person = content.data;
    const parentModel = this.modelFor('authenticated.syf') as AuthenticatedSyfRouteModel;
    const matchParticipant = parentModel.matchParticipant;
    const matchPlan = matchParticipant.matchPlan;
    return { person, matchParticipant, matchPlan };
  }
}
